import { defineStore } from 'pinia';

import DashboardsAPI from '@/apis/dashboards';
import { useAuthStore } from '@/stores/auth';
import { useDashboardsStore } from '@/stores/dashboards';

export const useDashboardsShareableLinksStore = defineStore('dashboardsShareableLinks', {
  state: () => ({
    dashboardShareableLinks: [],
    usersShareableLinks: [],
    adminShareableLinks: [],
    sharedDashboardLink: null,
    pending: {
      getAll: false,
      create: false,
      update: false,
      delete: false,
      getUserShareableLinks: false,
      getAdminShareableLinks: false,
    },
    visibleForDashboard: false,
  }),
  actions: {
    async getShareableDashboardLinks() {
      this.pending.getAll = true;
      const authStore = useAuthStore();
      const dashboardsStore = useDashboardsStore();
      const userId = authStore.identity?.id;
      const dashboardId = dashboardsStore.currentDashboard?.id;
      let response;
      try {
        response = await DashboardsAPI.getShareableDashboardLinks({
          userId,
          dashboardId,
        });
        this.visibleForDashboard = true;
      } catch (error) {
        if (error.response?.status === 403) {
          this.visibleForDashboard = false;
        } else {
          throw error;
        }
      } finally {
        this.pending.getAll = false;
      }
      this.dashboardShareableLinks = response?.data?.data || [];
      return response?.data?.data;
    },
    async getShareableDashboardLink({ shareableLinkId }) {
      const response = await DashboardsAPI.getShareableDashboardLink({ shareableLinkId });
      this.sharedDashboardLink = response?.data;
      return response?.data;
    },
    async createShareableDashboardLink({
      name,
      allowReportingPeriodChange,
      allowFilterChange,
      defaultFilters,
    }) {
      this.pending.create = true;
      const dashboardsStore = useDashboardsStore();
      const dashboardId = dashboardsStore.currentDashboard?.id;
      let response;
      try {
        response = await DashboardsAPI.createShareableDashboardLink({
          dashboardId,
          name,
          allowReportingPeriodChange,
          allowFilterChange,
          defaultFilters,
        });
        await this.getShareableDashboardLinks();
      } finally {
        this.pending.create = false;
      }
      return response?.data;
    },
    async updateShareableDashboardLink({
      shareableLinkId,
      name,
      enabled,
      validUntil,
      allowReportingPeriodChange,
      allowFilterChange,
      defaultFilters,
    }) {
      this.pending.update = true;
      let response;
      try {
        response = await DashboardsAPI.updateShareableDashboardLink({
          shareableLinkId,
          name,
          enabled,
          validUntil,
          allowReportingPeriodChange,
          allowFilterChange,
          defaultFilters,
        });
        await this.getShareableDashboardLinks();
        const authStore = useAuthStore();
        if (this.usersShareableLinks.length > 0) {
          await this.getUserShareableLinks({ userId: authStore.identity?.id });
        }
        if (this.adminShareableLinks.length > 0) {
          await this.getAdminShareableLinks({ brandId: authStore.currentBrand?.id });
        }
      } finally {
        this.pending.update = false;
      }
      return response?.data;
    },
    async deleteShareableDashboardLink({ shareableLinkId }) {
      this.pending.delete = true;
      let response;
      try {
        response = await DashboardsAPI.deleteShareableDashboardLink({
          shareableLinkId,
        });
        await this.getShareableDashboardLinks();
        const authStore = useAuthStore();
        if (this.usersShareableLinks.length > 0) {
          await this.getUserShareableLinks({ userId: authStore.identity?.id });
        }
        if (this.adminShareableLinks.length > 0) {
          await this.getAdminShareableLinks({ brandId: authStore.currentBrand?.id });
        }
      } finally {
        this.pending.delete = false;
      }
      return response?.data;
    },
    async getUserShareableLinks({ userId }) {
      this.pending.getUserShareableLinks = true;
      try {
        const response = await DashboardsAPI.getShareableDashboardLinks({ userId });
        this.usersShareableLinks = response?.data?.data || [];
      } finally {
        this.pending.getUserShareableLinks = false;
      }
    },
    async getAdminShareableLinks({ brandId }) {
      this.pending.getAdminShareableLinks = true;
      try {
        const response = await DashboardsAPI.getShareableDashboardLinks({ brandId });
        const authStore = useAuthStore();
        this.adminShareableLinks = (response?.data?.data || []).filter(
          (link) => link.userId !== authStore.identity?.id,
        );
      } finally {
        this.pending.getAdminShareableLinks = false;
      }
    },
  },
});
